import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, CheckCircle2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Captcha from "@/components/Captcha";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) { toast.error("Please complete the captcha"); return; }
    setBusy(true);
    try {
      await api.post("/auth/password/forgot", {
        email: email.trim(),
        captcha_token: captchaToken,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
      setCaptchaToken("");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="forgot-page">
      <div className="w-full max-w-md glass-strong rounded-3xl p-8 sm:p-10">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>
        {sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#32D74B]/20 border border-[#32D74B]/40 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#32D74B]" />
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">Check your inbox</h1>
            <p className="text-zinc-400 text-sm mt-3">
              If an account exists for <strong className="text-white">{email}</strong>, we just sent a password reset link.
              The link expires in 1 hour.
            </p>
            <p className="text-xs text-zinc-500 mt-6">
              Didn't receive anything? Check your spam folder, or{" "}
              <button onClick={() => { setSent(false); setCaptchaToken(""); }} className="text-[#E50914] font-semibold hover:underline">try again</button>.
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-7">
              <h1 className="font-display text-3xl font-black tracking-tight">Forgot password?</h1>
              <p className="text-zinc-400 text-sm mt-2">Type your email and we'll send you a reset link.</p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none"
                />
              </div>
              <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken("")} />
              <button
                data-testid="forgot-submit"
                type="submit"
                disabled={busy || !captchaToken}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E50914] hover:bg-[#F6121D] text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" /> {busy ? "Sending..." : "Send reset link"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
